/**
 * New organisations - the review queue.
 *
 * DELIBERATELY NOT THE BULK MAPPER. That lives under Integrations, beside the
 * PSA company list it depends on, because mapping seventy-three organisations
 * is a setup job you do once. This is the other half: the one new practice that
 * turned up on Tuesday, which is invisible if it has to be found among
 * seventy-three rows somebody already decided about.
 *
 * A new organisation is not an error and does not break anything - it appears
 * on the board and it alerts like any other. It simply files its tickets to the
 * default company until somebody says who it is. Saying so plainly is what
 * stops this reading as a fault.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

interface Alias { source: string; value: string }
interface Suggestion { id: string; name: string; score: number }

interface Client {
  id: string;
  name: string;
  aliases: Alias[];
  psaCompanyId: string | null;
  psaCompanyName: string | null;
  confirmed: boolean;
}

interface Review {
  unmatched: { source: string; client: string; suggestions: Suggestion[] }[];
  unconfirmed: { id: string; name: string }[];
  total: number;
}

const listClients = call<{ orgId: string }, { clients: Client[]; review: Review; duplicates?: DupGroup[] }>('listClients');
const mergeClients = call<{ orgId: string; intoId: string; fromIds: string[] }, { moved: number; removed: number }>('mergeClients');

interface DupGroup { key: string; name: string; ids: string[] }
const linkAlias = call<{ orgId: string; clientId: string; source: string; client: string }, { ok: boolean }>('linkAlias');

const SOURCE_LABEL: Record<string, string> = {
  datto: 'Datto', sp: 'ShadowProtect', sc: 'Image Manager',
  msp: 'MSP360', unitrends: 'Kaseya', email: 'Emailed',
};

export function ClientsReview({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [review, setReview] = useState<Review | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await listClients({ orgId });
      setClients(r.clients);
      setReview(r.review);
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setErr(null); setMsg(null);
    try { setMsg(await fn()); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  // Nothing new? Then this section has nothing to say, and says nothing. A
  // review queue that always shows a panel is one people stop reading.
  if (!review || review.unmatched.length === 0) return null;

  const n = review.unmatched.length;

  return (
    <section className="panel" style={{ marginTop: '1rem' }}>
      <h3 className="panel-title">
        New organisations
        <span className="tag tag-warn" style={{ marginLeft: 8 }}>{n}</span>
      </h3>
      <p className="panel-note" style={{ marginTop: 0 }}>
        {n === 1 ? 'This organisation has' : 'These organisations have'} appeared from a backup
        vendor and {n === 1 ? 'is' : 'are'} not matched to a company in your PSA yet.
        {' '}{n === 1 ? 'It is' : 'They are'} still monitored and still alert — tickets just go to
        the default company until you say who {n === 1 ? 'it is' : 'they are'}.
      </p>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      <table className="mini-table">
        <thead>
          <tr><th>Organisation</th><th>Source</th><th>Looks like</th></tr>
        </thead>
        <tbody>
          {review.unmatched.map((u) => (
            <tr key={`${u.source}:${u.client}`}>
              <td className="mono">{u.client}</td>
              <td className="dim">{SOURCE_LABEL[u.source] ?? u.source}</td>
              <td>
                {u.suggestions.length === 0 ? (
                  <span className="dim">no close match</span>
                ) : (
                  u.suggestions.map((s) => (
                    <button
                      key={s.id} className="btn-link" style={{ marginRight: 10 }}
                      disabled={busy !== null || !canManage}
                      title={`${Math.round(s.score * 100)}% similar - check it before accepting`}
                      onClick={() => void run('link' + s.id, async () => {
                        await linkAlias({ orgId, clientId: s.id, source: u.source, client: u.client });
                        return `"${u.client}" is now part of ${s.name}.`;
                      })}
                    >
                      {s.name} <span className="dim">{Math.round(s.score * 100)}%</span>
                    </button>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="panel-note">
        To choose a PSA company for {n === 1 ? 'it' : 'these'}, or to change any existing mapping,
        use <strong>Map organisations</strong> under Integrations &rarr; PSA ticketing.
        {clients.length > 0 && ` ${clients.length} organisation group(s) are already mapped.`}
      </p>
    </section>
  );
}

/**
 * The same customer, recorded more than once.
 *
 * WHY THIS IS NOT A TIDINESS FEATURE. Nothing enforces a unique client name,
 * and the adopt path creates a document per vendor spelling - so one customer
 * can hold three records, each owning a share of the machines. Their portal is
 * then built from ONE of those records and shows part of their estate with
 * nothing to suggest the rest exists. A portal that is confidently wrong is
 * worse than one that fails.
 *
 * The merge is offered, never applied. Two sites of a chain that genuinely
 * share a name is a judgement no rule should make unattended - so this suggests
 * and a human presses.
 */
export function DuplicateClients({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const [dups, setDups] = useState<DupGroup[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await listClients({ orgId });
      setDups(r.duplicates ?? []);
      setClients(r.clients);
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  if (dups.length === 0) return null;

  const nameOf = (id: string) => clients.find((c) => c.id === id)?.name ?? id;

  async function merge(g: DupGroup) {
    setBusy(g.key); setErr(null); setMsg(null);
    try {
      // KEEP THE FIRST, which is the oldest record by document order - the one
      // most likely to carry the mappings and history somebody has already
      // curated. Everything else folds into it, machines and aliases included.
      const [intoId, ...fromIds] = g.ids;
      const r = await mergeClients({ orgId, intoId: intoId!, fromIds });
      setMsg(
        `Merged ${fromIds.length + 1} records of "${nameOf(intoId!)}" into one, ` +
        `moving ${r.moved} machine${r.moved === 1 ? '' : 's'}.`,
      );
      await load();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <section className="panel" style={{ marginTop: '1rem' }}>
      <h3 className="panel-title">
        Clients recorded more than once
        <span className="tag tag-warn" style={{ marginLeft: 8 }}>{dups.length}</span>
      </h3>
      <p className="panel-note" style={{ marginTop: 0 }}>
        Each of these looks like one customer held as several records, usually because two
        backup vendors spell their name differently. Every record owns some of the machines, so
        a client portal built from one of them shows only part of that customer&apos;s estate.
        Merging moves the machines and keeps every vendor spelling.
      </p>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      <table className="mini-table">
        <thead>
          <tr><th>Client</th><th>Records</th><th></th></tr>
        </thead>
        <tbody>
          {dups.map((g) => (
            <tr key={g.key}>
              <td>
                <div className="cell-name">{g.name}</div>
                {/* Every spelling, so somebody can see WHY these grouped before
                    agreeing that they should. */}
                <div className="dim" style={{ fontSize: 11 }}>
                  {g.ids.map(nameOf).join('  |  ')}
                </div>
              </td>
              <td className="mono">{g.ids.length}</td>
              <td style={{ textAlign: 'right' }}>
                {canManage && (
                  <button
                    className="btn-link"
                    disabled={busy !== null}
                    onClick={() => void merge(g)}
                  >
                    {busy === g.key ? 'Merging...' : 'Merge into one'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}